import {
  faEllipsisV,
  faFilter,
  faPen,
  faPlus,
  faRotate,
  faTrash,
  faTrashAlt,
  faWrench
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-community';
import { useAtom } from 'jotai';
import type React from 'react';
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';

import ContextMenu, { type ContextMenuRefType } from '@/components/ContextMenu';
import DeviceStatusChip from '@/components/DeviceStatusChip';
import PERMISSIONS from '@/constants/auth.constants';
import { useDeviceDialog } from '@/features/DeviceCreate/DeviceCreate.hooks';
import { useLogSourcesDialog } from '@/features/LogSourcesCreate/LogSourcesCreate.hooks';
import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';
import { useCrudGuard } from '@/pages/Administration/Administration.hooks';
import { DEVICE_STATUS } from '@/pages/Administration/Devices/Devices.constants';
import {
  calculateStatusFromStatusCode,
  getStatusColor
} from '@/pages/Administration/Devices/Devices.service';
import { selectedDeviceAtom } from '@/pages/Administration/Devices/Devices.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import checkPermissions from '@/services/permissions';
import userAtom from '@/store/user.store';
import { textRenderer } from '@/utils/GridRenderer.utils';

import { DeviceSourceStatusChips } from './components';
import CellRendererErrorBoundary from './components/CellRendererErrorBoundary';
import StatusIconWithTooltip from './components/StatusIconWithTooltip';
import {
  DEVICE_TYPE_LIMA,
  DEVICE_TYPES_CONFIG
} from './DevicesTable.deviceTypes';
import useStyles from './DevicesTable.styles';
import type { DeviceTreeNode } from './DevicesTable.types';
import { buildCloudSourcesFilterUrl } from './utils/filterUrl.utils';

/**
 * Auto-group column inner renderer for device/source names.
 */
export const DeviceNameInnerCellRenderer = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  const { data } = params;
  const { classes } = useStyles();

  if (!data) return null;

  const tooltipClasses = {
    arrow: classes.routeButtonTooltipArrow,
    tooltip: classes.routeButtonTooltip
  };

  return (
    <Stack className="agDeviceStatusIconStack" direction="row">
      {data.type === 'device' && (
        <StatusIconWithTooltip
          status={data.status!}
          statusLabel={data.statusLabel}
          deviceType={data.deviceType || ''}
          tooltipClasses={tooltipClasses}
          iconClassName={classes.deviceStatusIconStyle}
        />
      )}
      {(data.type === 'source' || data.type === 'provider') && (
        <StatusIconWithTooltip
          status={data.status!}
          statusLabel={data.statusLabel}
          deviceType="Cloud Collector"
          tooltipClasses={tooltipClasses}
          iconClassName={classes.deviceStatusIconStyle}
        />
      )}

      {data.type === 'device' && (
        <Box className={classes.icoLabelHeaderStyle}>
          {data?.deviceData?.display_name &&
          data.deviceData.display_name.length > 0 ? (
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
              <Typography
                style={{ alignContent: 'center' }}
                color="text.secondary"
              >
                {data.deviceData.host_name}
              </Typography>
              <Typography
                style={{ alignContent: 'center' }}
                color="text.secondary"
              >
                [{data.deviceData.display_name}]
              </Typography>
            </Stack>
          ) : (
            data.deviceData?.host_name
          )}
        </Box>
      )}
      {data.type !== 'device' && (
        <Tooltip title={data?.name}>
          <span className={classes.icoLabelStyle}>{data.name}</span>
        </Tooltip>
      )}
    </Stack>
  );
};

const renderTextCellWithBoundary = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  return (
    <CellRendererErrorBoundary>
      {textRenderer(params)}
    </CellRendererErrorBoundary>
  );
};

/**
 * Renders the device type column.
 */
export const renderDeviceTypeCell = renderTextCellWithBoundary;

/**
 * Renders the IP address column.
 */
export const renderIpAddressCell = renderTextCellWithBoundary;

/**
 * Renders device/source status with sync indicators and action icons.
 */
export const StatusCellRenderer = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  const { data } = params;
  const { classes } = useStyles();

  if (!data) return null;

  return (
    <CellRendererErrorBoundary>
      <Tooltip
        arrow
        placement="left"
        title={`Last updated ${data.statusUpdatedAt || ''}`}
        classes={{
          arrow: classes.routeButtonTooltipArrow,
          tooltip: classes.routeButtonTooltip
        }}
      >
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <DeviceStatusChip status={data.status ?? ''} />

          {data.type === 'device' &&
            !data.isSyncInProgress &&
            data.deviceData?.device_type_id !== DEVICE_TYPE_LIMA && (
              <>
                {data.actionType === 'Updated' && (
                  <FontAwesomeIcon
                    icon={faWrench}
                    size="sm"
                    style={{ marginLeft: '.5rem' }}
                  />
                )}
                {data.actionType === 'Deleted' && (
                  <FontAwesomeIcon
                    icon={faTrash}
                    size="sm"
                    style={{ marginLeft: '.5rem' }}
                  />
                )}
              </>
            )}

          {data.type === 'device' &&
            data.isSyncInProgress &&
            data.deviceData?.device_type_id !== DEVICE_TYPE_LIMA && (
              <FontAwesomeIcon
                style={{ marginLeft: '.5rem' }}
                icon={faRotate}
                size="sm"
                className={classes.warningColor}
              />
            )}
        </Box>
      </Tooltip>
    </CellRendererErrorBoundary>
  );
};

/**
 * Renders the last connection/last seen column.
 */
export const renderLastSeenCell = renderTextCellWithBoundary;

/**
 * Renders the organization column.
 */
export const renderOrganizationCell = renderTextCellWithBoundary;

/**
 * Renders the sources count column with status chips.
 */
export const SourcesCountCellRenderer = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  const { data } = params;
  const { classes } = useStyles();

  if (!data || data.status === undefined) return null;

  if (
    data.type === 'device' &&
    data.sourceCount !== undefined &&
    data.sourceCount > 0
  ) {
    return (
      <Stack direction="row">
        <Chip
          label={`${data.sourceCount}`}
          variant="filled"
          color={getStatusColor(data.sourceStatus || 'unknown')}
          size="small"
        />
      </Stack>
    );
  }

  if (
    data.type === 'source' &&
    data.source_type_id !== SOURCE_TYPE.MICROSOFT &&
    data.providerData
  ) {
    return <DeviceSourceStatusChips row={data.providerData} />;
  }

  if (data.type === 'source' && data.source_type_id === SOURCE_TYPE.MICROSOFT) {
    return (
      <Chip
        key={data.source_type_id}
        className={classes.chipMarginGap}
        label={`${data.sourceCount}`}
        variant="filled"
        color={getStatusColor(
          calculateStatusFromStatusCode(
            data.highestStatusCode?.toString() || ''
          )
        )}
        size="small"
      />
    );
  }

  if (data.type === 'provider' && data.sourceCount !== undefined) {
    return (
      <Stack direction="row">
        {data.providerData && (
          <DeviceSourceStatusChips row={data.providerData} />
        )}
      </Stack>
    );
  }

  return null;
};

const DeviceActionsCellRenderer = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  const { data } = params;
  const { classes } = useStyles();
  const childRef = useRef<ContextMenuRefType>(null);

  const [user] = useAtom(userAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setSelectedDevice] = useAtom(selectedDeviceAtom);

  const { openDeviceDialog } = useDeviceDialog();
  const { openLogSourcesDialog } = useLogSourcesDialog();

  const excludedStatuses = [
    DEVICE_STATUS.DECOMMISSIONING,
    DEVICE_STATUS.CONFIG_SUCCESS,
    DEVICE_STATUS.CONFIG_FAILED,
    DEVICE_STATUS.DEPLOYMENT_FAILED,
    DEVICE_STATUS.JOB_FAILED
  ];

  const editDeviceHandler = useCrudGuard(() => {
    if (data?.deviceData) {
      openDeviceDialog('Edit', data.deviceData.device_uid);
    }
  });

  const deleteDeviceHandler = useCrudGuard(() => {
    if (data?.deviceData) {
      openDeviceDialog('Delete', data.deviceData.device_uid);
    }
  });

  const addLogSourcesHandler = useCrudGuard(() => {
    if (data?.deviceData) {
      openLogSourcesDialog('AddLogSources', data.deviceData.device_uid);
    }
  });

  const editLogSourcesHandler = useCrudGuard(() => {
    if (data?.deviceData) {
      openLogSourcesDialog('EditLogSources', data.deviceData.device_uid);
    }
  });

  const menuList = useMemo(() => {
    const list: {
      label: string;
      icon: typeof faPen;
      permission: string;
    }[] = [];

    if (data?.type !== 'device' || !data.deviceData) {
      return list;
    }

    const selectedDevice = data.deviceData;

    if (data.actionType === 'Deleted') {
      return list;
    }

    const deviceTypesConfig = DEVICE_TYPES_CONFIG.find(
      (item) => item.id === selectedDevice.device_type_id
    );

    if (
      !excludedStatuses.includes(selectedDevice.status as DEVICE_STATUS) &&
      selectedDevice.device_type_id !== DEVICE_TYPE_LIMA
    ) {
      list.push({
        label: 'Edit',
        icon: faPen,
        permission: PERMISSIONS.DEVICES_UPDATE
      });
      list.push({
        label: 'Delete',
        icon: faTrashAlt,
        permission: PERMISSIONS.DEVICES_DELETE
      });
    }

    if (deviceTypesConfig?.hasSource && selectedDevice.device_type_id === 2) {
      list.push({
        label: `${
          (selectedDevice.sources?.length ?? 0) > 0 ? 'Edit' : 'Add'
        } Log Sources`,
        icon: faPlus,
        permission: PERMISSIONS.DEVICES_CREATE
      });
    }

    return list;
  }, [data, excludedStatuses]);

  const accessCount = useMemo(() => {
    return menuList.filter((item: { permission: string }) =>
      checkPermissions(item.permission, user, selectedOrganization)
    ).length;
  }, [menuList, user, selectedOrganization]);

  if (data?.type !== 'device' || !data.deviceData) {
    return null;
  }

  const selectedDevice = data.deviceData;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedDevice(selectedDevice);
    childRef.current?.showContextMenu(event.currentTarget);
  };

  const onClickHandler = (selectedMenu: string) => {
    switch (selectedMenu) {
      case 'Edit':
        editDeviceHandler();
        break;
      case 'Delete':
        deleteDeviceHandler();
        break;
      case 'Add Log Sources':
        addLogSourcesHandler();
        break;
      case 'Edit Log Sources':
        editLogSourcesHandler();
        break;
      default:
        break;
    }
  };

  return accessCount > 0 ? (
    <IconButton size="small" className="action-btn" onClick={handleClick}>
      <FontAwesomeIcon icon={faEllipsisV} size="xs" />

      <ContextMenu
        ref={childRef}
        menuList={menuList}
        className={classes.ellipsisMenu}
        clickHandler={onClickHandler}
      />
    </IconButton>
  ) : null;
};

const SourceActionsCellRenderer = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  const { data } = params;
  const navigate = useNavigate();

  if (!data) return null;

  if (data.type !== 'source' || !data.providerData) {
    return null;
  }

  const handleFilterClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const sourceTypeId = data.providerData?.source_type_id;
    if (!sourceTypeId) return;

    navigate(buildCloudSourcesFilterUrl(sourceTypeId));
  };

  return (
    <IconButton
      size="small"
      onClick={handleFilterClick}
      aria-label="Filter cloud sources by type"
    >
      <FontAwesomeIcon icon={faFilter} size="xs" aria-hidden="true" />
    </IconButton>
  );
};

/**
 * Combined Actions cell renderer that handles both devices and sources.
 */
export const renderActionsCell = (
  params: ICellRendererParams<DeviceTreeNode>
) => {
  const { data } = params;

  if (!data) return null;

  if (data.type === 'device') {
    return <DeviceActionsCellRenderer {...params} />;
  }

  if (data.type === 'source' || data.type === 'provider') {
    return <SourceActionsCellRenderer {...params} />;
  }

  return null;
};
